import React from "react";
import axios from "axios";
import { X, AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// pdf.js worker — the file is bundled by webpack via `new URL(..., import.meta.url)`
// but on production servers that don't map .mjs to application/javascript
// (many CDNs / static servers default to application/octet-stream), the
// browser refuses to load it as a module and pdf.js falls back to the fake
// worker inside the main thread — which then crashes the render.
//
// Bypass the MIME dance: fetch the raw .mjs (fetch ignores MIME), wrap the
// code in a Blob with the correct application/javascript MIME, then hand
// that Blob's URL to pdf.js as workerSrc. pdf.js instantiates a fresh
// Worker per <Document> — each one loads from OUR Blob URL, which the
// browser serves with a valid JS MIME regardless of what the static server
// does. We deliberately do NOT use workerPort here because that would make
// the Worker a singleton that react-pdf terminates on <Document> unmount,
// breaking every re-open.
// Reference the pristine worker copied verbatim into /public (never processed by
// webpack/Babel), instead of new URL(...import.meta.url) which routes the worker
// THROUGH webpack — that transform rewrote its @babel/runtime helper imports to
// build-machine absolute paths (/workspace/...), breaking the worker on iOS Safari.
const _originalWorkerUrl = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

pdfjs.GlobalWorkerOptions.workerSrc = _originalWorkerUrl;

const _workerReady = (async () => {
  try {
    const resp = await fetch(_originalWorkerUrl, { credentials: "omit" });
    if (!resp.ok) throw new Error(`worker fetch ${resp.status}`);
    const code = await resp.text();
    const blob = new Blob([code], { type: "application/javascript" });
    pdfjs.GlobalWorkerOptions.workerSrc = URL.createObjectURL(blob);
    return true;
  } catch {
    return false;
  }
})();

// ============================================================================
//  ⚠️  DO NOT EDIT WITHOUT READING THIS — PDF VIEWER PROTECTION
// ============================================================================
//  react-pdf compares the <Document> `options` prop by REFERENCE. If you pass
//  an inline object literal like:
//      <Document options={{ cMapUrl: "…", cMapPacked: true }} />
//  a NEW object is created every render → react-pdf destroys and rebuilds its
//  pdf.js WorkerTransport → any <Page> whose async render() is still in flight
//  dereferences a null transport → hard crash:
//      TypeError: Cannot read properties of null (reading 'sendWithPromise')
//  and the whole PDF area goes BLANK. This has broken the app twice already.
//
//  RULES to keep the PDF viewer working:
//   1. NEVER pass an inline object to `options`. Always import the constant
//      PDF_DOCUMENT_OPTIONS below.
//   2. NEVER pass an inline object to `file` either — memoise via useMemo.
//   3. Do NOT unmount the <PdfViewer> to hide it. It stays mounted and toggles
//      via `style.display` so the pdf.js worker survives close/reopen cycles.
//   4. If you change these props, run the E2E: open PDF on A001 → close →
//      reopen → next song → verify 0 `sendWithPromise` errors in console.
// ============================================================================
const PDF_DOCUMENT_OPTIONS = Object.freeze({
  cMapUrl: "https://unpkg.com/pdfjs-dist@5.4.296/cmaps/",
  cMapPacked: true,
});

/**
 * PdfViewer — renders the PDF natively in-page via pdf.js / react-pdf so the
 * browser's "icon + filename + Ouvrir" fallback (Android Chrome's default
 * behaviour for embedded PDFs) is never shown.
 *
 * The PDF is rendered as canvas, with all pages stacked vertically so the
 * user can scroll lyrics like a real sheet.
 */

// Local error boundary — any worker or render crash inside the <Document>
// tree is caught here and swapped for a friendly retry message, so a
// single failure never wipes the entire app UI.
class PdfErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err) {
    // eslint-disable-next-line no-console
    console.error("[PDF] worker/render error", err?.message || err);
  }
  reset = () => this.setState({ hasError: false });
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center text-neutral-700 px-6 gap-3 bg-neutral-100" data-testid="pdf-error-boundary">
          <div className="text-lg font-bold text-red-600">PDF momentanément indisponible</div>
          <div className="text-sm text-neutral-600 max-w-sm">
            Le moteur pdf.js a rencontré une erreur. Ferme puis rouvre les paroles pour réessayer.
          </div>
          <button
            onClick={() => { this.reset(); this.props.onReset?.(); }}
            className="mt-2 px-4 py-2 rounded-lg bg-[var(--gl-gold)] text-black font-bold"
          >
            Réessayer
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export const PdfViewer = ({
  url,
  songId,
  open,
  onClose,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  prevId,
  nextId,
}) => {
  const swipeStartRef = React.useRef(null);
  const [transitionHint, setTransitionHint] = React.useState(null);
  const [workerReady, setWorkerReady] = React.useState(false);

  // Wait for the Blob-URL worker to be ready before mounting <Document>.
  // Without this, the very first render on a slow production server can
  // race against the .mjs fetch and pdf.js falls back to the crashing
  // fake worker.
  React.useEffect(() => {
    let cancelled = false;
    _workerReady.then(() => {
      if (!cancelled) setWorkerReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const [numPages, setNumPages] = React.useState(0);
  const [pageWidth, setPageWidth] = React.useState(800);
  const containerRef = React.useRef(null);
  const [loadError, setLoadError] = React.useState(false);

  // Fetch the PDF as an authenticated Blob via axios (same auth flow as the
  // chord-sheet exporter). We deliberately do NOT hand a URL to react-pdf,
  // because:
  //   1. It bypasses any cache-first Service Worker layer that might return
  //      a stale/HTML shell for /api/files/pdf/*.
  //   2. It uses the app-wide axios instance (cookies + Bearer interceptor),
  //      so we don't need the ?t=<jwt> query-token hack.
  //   3. The `<Document file={{ data: ArrayBuffer }} />` path in react-pdf
  //      is the most stable — no cross-origin or redirect surprises.
  const [pdfArrayBuffer, setPdfArrayBuffer] = React.useState(null);
  const [fetching, setFetching] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    if (!url) {
      setPdfArrayBuffer(null);
      setLoadError(false);
      return () => { cancelled = true; };
    }
    setFetching(true);
    setLoadError(false);
    setPdfArrayBuffer(null);
    (async () => {
      try {
        const res = await axios.get(url, { responseType: "blob" });
        const buf = await res.data.arrayBuffer();
        if (!cancelled) {
          setPdfArrayBuffer(buf);
          setFetching(false);
        }
      } catch (e) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error("[PDF] fetch failed", e?.message || e);
          setLoadError(true);
          setFetching(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  // react-pdf accepts { data: Uint8Array/ArrayBuffer } as an in-memory source.
  // Memoise so react-pdf doesn't restart the load on every render.
  const fileSource = React.useMemo(
    () => (pdfArrayBuffer ? { data: pdfArrayBuffer } : null),
    [pdfArrayBuffer]
  );

  // Resize observer → compute page width once based on the container width.
  React.useEffect(() => {
    if (!containerRef.current) return undefined;
    const el = containerRef.current;
    const update = () => {
      const w = el.clientWidth;
      if (w) setPageWidth(Math.min(1100, w - 16));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open]);

  // Reset numPages whenever the URL changes (loadError is reset inside the
  // fetch effect above so we don't duplicate it here).
  React.useEffect(() => {
    setNumPages(0);
  }, [url]);

  const onPointerDown = (e) => {
    if (e.target?.tagName?.toLowerCase() === "canvas") {
      // Still allow swipe on top of the canvas, but record start.
    }
    swipeStartRef.current = { x: e.clientX, y: e.clientY, t: performance.now() };
  };
  const onPointerUp = (e) => {
    const start = swipeStartRef.current;
    swipeStartRef.current = null;
    if (!start) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    const dt = performance.now() - start.t;
    if (Math.abs(dx) > 70 && Math.abs(dy) < 60 && dt < 600) {
      if (dx < 0 && hasNext) {
        setTransitionHint("left");
        onNext?.();
      } else if (dx > 0 && hasPrev) {
        setTransitionHint("right");
        onPrev?.();
      }
      setTimeout(() => setTransitionHint(null), 250);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black flex flex-col"
      data-testid="pdf-viewer"
      style={{ display: open ? "flex" : "none" }}
      aria-hidden={!open}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black shrink-0">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="text-[var(--gl-gold)] font-bold tabular-nums">{songId}</span>
          <span className="text-white/60 text-sm truncate">Paroles PDF</span>
          {numPages > 0 && (
            <span className="text-white/30 text-xs tabular-nums ml-2">
              {numPages} page{numPages > 1 ? "s" : ""}
            </span>
          )}
        </div>
        <div className="hidden sm:flex items-center gap-1 mr-2">
          <button
            onClick={onPrev}
            disabled={!hasPrev}
            className="w-9 h-9 rounded-md border border-white/15 bg-black flex items-center justify-center text-white hover:border-white/40 disabled:opacity-30"
            data-testid="pdf-prev-btn"
            title={prevId ? `← ${prevId}` : "Aucun morceau précédent"}
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={onNext}
            disabled={!hasNext}
            className="w-9 h-9 rounded-md border border-white/15 bg-black flex items-center justify-center text-white hover:border-white/40 disabled:opacity-30"
            data-testid="pdf-next-btn"
            title={nextId ? `${nextId} →` : "Aucun morceau suivant"}
          >
            <ChevronRight size={16} />
          </button>
        </div>
        <button
          onClick={onClose}
          className="relative z-[90] tap-lg px-4 rounded-lg bg-[var(--gl-gold)] text-black font-bold flex items-center gap-2 hover:brightness-110 transition shrink-0"
          data-testid="close-pdf-btn"
        >
          <X size={18} /> Fermer
        </button>
      </div>

      {/* PDF body — canvas-rendered pages (no browser PDF viewer fallback) */}
      <div
        ref={containerRef}
        className={`flex-1 overflow-y-auto bg-neutral-100 transition-transform duration-200 ${
          transitionHint === "left"
            ? "-translate-x-2 opacity-90"
            : transitionHint === "right"
              ? "translate-x-2 opacity-90"
              : ""
        }`}
        data-testid="pdf-canvas-area"
      >
        {fileSource && !loadError && workerReady ? (
          <PdfErrorBoundary onReset={() => setLoadError(false)}>
            <Document
              file={fileSource}
              onLoadSuccess={({ numPages: n }) => {
                setNumPages(n);
                setLoadError(false);
              }}
              onLoadError={() => setLoadError(true)}
              loading={
                <div className="text-white/60 text-sm text-center py-10">
                  Chargement de la partition…
                </div>
              }
              options={PDF_DOCUMENT_OPTIONS}
            >
              <div className="flex flex-col items-center gap-3 py-3">
                {Array.from({ length: numPages }, (_, i) => (
                  <Page
                    key={i + 1}
                    pageNumber={i + 1}
                    width={pageWidth}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    className="shadow-lg"
                  />
                ))}
              </div>
            </Document>
          </PdfErrorBoundary>
        ) : fileSource && !workerReady ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-neutral-700 px-6 gap-3 bg-neutral-100" data-testid="pdf-worker-loading">
            <div className="animate-pulse text-lg font-bold">Initialisation du moteur PDF…</div>
            <div className="text-sm text-neutral-500">Un instant, chargement du worker pdf.js.</div>
          </div>
        ) : url && fetching && !loadError ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-neutral-700 px-6 gap-3 bg-neutral-100" data-testid="pdf-fetching">
            <div className="animate-pulse text-lg font-bold">Téléchargement du PDF…</div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center text-neutral-700 px-6 gap-3 bg-neutral-100">
            <AlertTriangle size={36} className="text-orange-500" />
            <div className="text-lg font-bold">
              {loadError ? "PDF illisible" : "Aucune partition disponible"}
            </div>
            <div className="text-sm text-neutral-600 max-w-sm">
              {loadError
                ? "Le fichier PDF n'a pas pu être ouvert. Vérifie son format."
                : "Ce morceau n'a pas de PDF. Tape « Charger » sur la page principale pour en ajouter un."}
            </div>
          </div>
        )}
      </div>

      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-widest text-white/40 pointer-events-none select-none">
        ← swipe pour changer de morceau →
      </div>
    </div>
  );
};

export default PdfViewer;
